import { Link, useParams } from 'react-router-dom';
import { useLead } from '../api/hooks';
import { TierBadge } from '../components/TierBadge';
import { RiskBadge } from '../components/RiskBadge';
import { StatusActions } from '../components/StatusActions';
import { LeadLocationMap } from '../components/LeadLocationMap';
import { SourceRow } from '../components/SourceRow';
import { zoneNameEl } from '../lib/zones';

const eur = new Intl.NumberFormat('el-GR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});
const fmtEur = (n: number | null): string => (n == null ? '—' : eur.format(n));
const pct = (n: number | null): string =>
  n == null ? '—' : `${Math.round(n * 100)}%`;

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: lead, isLoading, isError, error } = useLead(id);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-1/3 animate-pulse rounded bg-slate-200" />
        <div className="h-32 animate-pulse rounded-lg bg-white" />
      </div>
    );
  }
  if (isError || !lead) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        Δεν βρέθηκε το lead. {(error as Error | null)?.message}
      </div>
    );
  }

  const latestScore = lead.scores[0] ?? null;
  const sqm = lead.sqmVerified ?? lead.sqm;
  const startingSqmPrice =
    lead.startingPrice != null && sqm != null && sqm > 0
      ? lead.startingPrice / sqm
      : null;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/feed"
          className="text-xs text-slate-500 transition hover:text-slate-900"
        >
          ← Πίσω στο Feed
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">
            {lead.municipality ?? lead.address ?? lead.auctionId}
          </h1>
          <TierBadge tier={latestScore?.tier ?? null} />
          <RiskBadge risk={lead.riskRating} />
          {lead.iauctionListingId != null && (
            <span
              className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700"
              title={`iAuction listing #${lead.iauctionListingId}`}
            >
              iAuction #{lead.iauctionListingId}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Auction code: {lead.auctionCode ?? lead.auctionId} · Status:{' '}
          {lead.status}
        </p>
      </div>

      <StatusActions leadId={lead.id} currentStatus={lead.status} />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card title="Ακίνητο">
          <Row label="Είδος" value={lead.propertyType} />
          <Row label="Επιφάνεια" value={sqm != null ? `${Math.round(sqm)} τμ` : null} />
          <Row label="Όροφος" value={floorLabel(lead.floor)} />
          <Row label="Έτος" value={lead.constructionYear} />
          <Row label="Διεύθυνση" value={lead.address} />
        </Card>
        <Card title="Δημοπρασία">
          <Row label="Τιμή εκκίνησης" value={fmtEur(lead.startingPrice)} />
          <Row
            label="€/τμ εκκίνησης"
            value={startingSqmPrice != null ? `${fmtEur(startingSqmPrice)}/τμ` : null}
          />
          <Row label="Γύρος" value={lead.roundNumber} />
          <Row label="Ημερομηνία" value={lead.auctionDate} />
          <Row label="Συμβολαιογράφος" value={lead.notaryName} />
          <Row label="Επισπεύδων" value={lead.creditorName} />
        </Card>
        <Card title="Αξιολόγηση">
          <Row label="Ιδιοκτησία" value={`${lead.ownershipPercent ?? '—'}%`} />
          <Row label="Εκτιμηθείσα αξία" value={fmtEur(lead.appraisedValue)} />
          <Row
            label="Εκτ. €/τμ"
            value={lead.appraisedSqmPrice != null ? fmtEur(lead.appraisedSqmPrice) : null}
          />
          <Row label="Red flags" value={lead.redFlagCount} />
          <Row
            label="iAuction"
            value={lead.iauctionListingId != null ? `Ναι · #${lead.iauctionListingId}` : 'Όχι'}
          />
        </Card>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Τοποθεσία</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="h-72 md:col-span-2">
            <LeadLocationMap
              lat={lead.geoLat}
              lng={lead.geoLng}
              zoneSlug={lead.zoneSlug}
            />
          </div>
          <Card title="Διεύθυνση">
            <Row label="Οδός" value={lead.streetAddress ?? lead.address} />
            <Row label="Τ.Κ." value={lead.postalCode} />
            <Row label="Περιοχή (L2)" value={zoneNameEl(lead.zoneL2Slug)} />
            <Row label="Γειτονιά (L3)" value={zoneNameEl(lead.zoneSlug)} />
            <Row
              label="Συντεταγμένες"
              value={
                lead.geoLat != null && lead.geoLng != null
                  ? `${lead.geoLat.toFixed(5)}, ${lead.geoLng.toFixed(5)}`
                  : null
              }
            />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          Βάση εκτίμησης
        </h2>
        {lead.scoreBasis ? (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Εκτιμώμενη αγορά (combo) · {lead.scoreBasis.sqmBand} τμ
                  {lead.scoreBasis.yearBand && lead.scoreBasis.yearBand !== '*'
                    ? ` · ${lead.scoreBasis.yearBand}`
                    : ''}
                </div>
                <div className="text-2xl font-semibold text-slate-900">
                  {lead.scoreBasis.mid != null
                    ? `${fmtEur(lead.scoreBasis.mid)}/m²`
                    : '—'}
                </div>
              </div>
              <div className="text-right text-xs text-slate-500">
                <div>Confidence {pct(lead.scoreBasis.confidence)}</div>
                <div className="mt-0.5">
                  Ζώνη: {zoneNameEl(lead.scoreBasis.zoneSlug) ?? '—'}
                </div>
              </div>
            </div>
            {lead.scoreBasis.zoneSlug !== lead.zoneSlug && (
              <p className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                Η τιμή προέκυψε από ζώνη/συνδυασμό fallback — δεν υπήρχαν επαρκή
                δεδομένα στην ακριβή γειτονιά/μπάντα του ακινήτου.
              </p>
            )}
            <div className="space-y-1.5 text-xs">
              <SourceRow
                label="Spitogatos (asking ×0.80)"
                value={lead.scoreBasis.sources.spitogatos.value}
                count={lead.scoreBasis.sources.spitogatos.count}
              />
              <SourceRow
                label="xe.gr (asking ×0.80)"
                value={lead.scoreBasis.sources.xe.value}
                count={lead.scoreBasis.sources.xe.count}
              />
              <SourceRow
                label="iauction R1 (appraised)"
                value={lead.scoreBasis.sources.iauctionR1.value}
                count={lead.scoreBasis.sources.iauctionR1.count}
              />
              <SourceRow
                label="iauction R3+ (stressed)"
                value={lead.scoreBasis.sources.iauctionR3.value}
                count={lead.scoreBasis.sources.iauctionR3.count}
              />
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
            — χωρίς βάση τιμής (δεν καταγράφηκε market price για το score).
          </div>
        )}
      </section>

      {lead.redFlagSummary && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h2 className="mb-1 text-sm font-semibold text-amber-900">
            Red flags
          </h2>
          <p className="text-sm text-amber-900">{lead.redFlagSummary}</p>
        </section>
      )}

      {lead.description && (
        <section>
          <h2 className="mb-1 text-sm font-semibold text-slate-700">
            Περιγραφή
          </h2>
          <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700 whitespace-pre-line">
            {lead.description}
          </p>
        </section>
      )}

      {lead.documents.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-slate-700">
            Έγγραφα ({lead.documents.length})
          </h2>
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {lead.documents.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between px-4 py-2 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-700">
                    {d.docType.replace(/_/g, ' ')}
                  </span>
                  {d.byteSize != null && (
                    <span className="text-xs text-slate-400">
                      {(d.byteSize / 1024).toFixed(0)} KB
                    </span>
                  )}
                </div>
                {d.sourceUrl && (
                  <a
                    href={d.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-rose-600 hover:underline"
                  >
                    Source ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-wrap gap-3">
        {lead.eauctionUrl && (
          <a
            href={lead.eauctionUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
          >
            eauction.gr ↗
          </a>
        )}
        {lead.mapsUrl && (
          <a
            href={lead.mapsUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Maps ↗
          </a>
        )}
      </section>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      <dl className="space-y-1 text-sm">{children}</dl>
    </div>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 text-right">
        {value == null || value === '' ? '—' : value}
      </dd>
    </div>
  );
}

function floorLabel(floor: number | null): string | null {
  if (floor == null) return null;
  if (floor === 0) return 'ισόγειο';
  if (floor < 0) return 'υπόγειο';
  return `${floor}ος`;
}
